import { candidateScoreOf } from '../../utils/convertQuestionnaire';

type RequestParameter = Http.Connection.Request.Parameter.EverydayNote.Admin.ControlUpdate
    | Http.Connection.Request.Parameter.EverydayNote.Admin.Create
    | Http.Connection.Request.Parameter.EverydayNote.Admin.Update;

/**
 * フォームの値をバックエンドに送るフォーマットに変換する
 */
export const convertSubmittableValue = (
    values: EverydayNote.Form.Admin.ControlQuestionnaire | EverydayNote.Form.Admin.AdminQuestionnaire,
    isCopy: boolean
): RequestParameter => {
    const {
        afterMessage,
        allowPastDateAnswer,
        canComment,
        description,
        duration,
        isHolidayAnswerDisallowed,
        isOnceADay,
        isRequired,
        isSetRecordableTime,
        isViewedHome,
        items,
        publishFrom,
        recordableTimeEnd,
        recordableTimeStart,
        recordableDayOfWeeks,
        uuid,
        name,
        explanation,
        inlineFiles,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ..._rest
    } = values;

    const base = {
        after_message: afterMessage,
        allow_past_date_answer: allowPastDateAnswer,
        can_comment: canComment,
        day_of_weeks: isSetRecordableTime ?
            recordableDayOfWeeks :
            [0, 1, 2, 3, 4, 5, 6],
        description: description,
        end_time: isSetRecordableTime ?
            recordableTimeEnd?.format('HH:mm') ?? '23:59' :
            '23:59',
        // 「回答可能な時間の指定」をしない場合は曜日と同様に祝日制限も外す
        is_holiday_answer_disallowed: isSetRecordableTime ? isHolidayAnswerDisallowed : false,
        is_once_a_day: isOnceADay,
        is_required: isRequired,
        is_viewed_home: isViewedHome,
        items: items.map(convertItem(isCopy)),
        name,
        // 保存時には必ず 公開終了時刻 が設定されるため、undefined とはならないが、
        // 適切な型付けが大変なので、一旦強制的に対応する
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        publish_end_at: duration[1]!.unix(),
        publish_from: publishFrom,
        publish_start_at: duration[0].unix(),
        start_time: isSetRecordableTime ?
            recordableTimeStart?.format('HH:mm') ?? '00:00' :
            '00:00',
        // TODO: 現状選択しないので固定値
        type: 1 as EverydayNote.QuestionnaireTypes,
        explanation: explanation,
        files: inlineFiles.map((uuid: string) => ({ file_uuid: uuid, is_inline: true }))
    };

    if ('targetOrganizations' in values) {
        if (uuid === undefined) throw new Error('uuid is required');

        return {
            day_of_weeks: base.day_of_weeks,
            end_time: base.end_time,
            is_holiday_answer_disallowed: base.is_holiday_answer_disallowed,
            is_viewed_home: base.is_viewed_home,
            publish_end_at: base.publish_end_at,
            publish_from: base.publish_from,
            publish_start_at: base.publish_start_at,
            start_time: base.start_time,
            uuid,
        };
    } else {
        const adminVal = { ...base, ...convertAdminValue(values) };

        return isCopy || uuid === undefined ?
            adminVal :
            { ...adminVal, uuid };
    }
};

// 学校管理でのみ使われる値を変換する
const convertAdminValue = (
    values: Expand<Omit<EverydayNote.Form.Admin.AdminQuestionnaire, keyof EverydayNote.Form.Admin.ControlQuestionnaire>>,
) => {
    const {
        targetRoles,
        targetSchoolClasses,
        targetUsers,
        ...rest
    } = values;

    // エラーチェック用に型を使うためのダミー
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _: Record<never, never> = rest;

    return {
        target_roles: targetRoles.map(e => e.uuid),
        target_school_classes: targetSchoolClasses.map(e => e.uuid),
        target_users: targetUsers.map(e => e.uuid),
    };
};

const convertItem = (isCopy: boolean) => (
    item: Parameters<typeof convertSubmittableValue>[0]['items'][number],
    index: number,
): Http.Connection.Request.Parameter.EverydayNote.Admin.Create['items'][number] => {
    const {
        candidates,
        evaluationScaleMax,
        isRequired,
        uuid,
        order,
        sliderRangeMax,
        sliderRangeMin,
        sliderInitialUserValue,
        sliderRangeMinDetail,
        sliderRangeMaxDetail,
        sliderInitialUserValueDetail,
        showSliderValueDetails,
        markDetails,
        candidateOrderPositive,
        ...restItem
    } = item;

    // 段階選択式（type=10）は序列設定と並び順から各選択肢のスコアを算出して送る
    const candidatesPayload = candidates.map((candidate, i) => ({
        content: candidate.content,
        order: candidate.order,
        uuid: isCopy ? undefined : candidate.uuid,
        ...(restItem.type === 10
            ? { score: candidateScoreOf(i, candidates.length, candidateOrderPositive ?? true) }
            : {}),
    }));

    return isCopy || uuid === undefined ?
        {
            candidates: candidatesPayload,
            is_required: isRequired,
            order: order ?? index,
            ...(restItem.type === 6 ? { 
                evaluation_scale_max: evaluationScaleMax,
                mark_details: markDetails ? markDetails : Array(evaluationScaleMax).fill(''),
            } : {}),
            ...(restItem.type === 8 ? { 
                slider_range_max: sliderRangeMax,
                slider_range_min: sliderRangeMin,
                slider_initial_user_value: sliderInitialUserValue,
                slider_range_max_detail: sliderRangeMaxDetail,
                slider_range_min_detail: sliderRangeMinDetail,
                slider_initial_user_value_detail: sliderInitialUserValueDetail,
                show_slider_value_details: showSliderValueDetails,
            } : {}),
            ...((restItem.type === 4) ? { 
                    mark_details: markDetails ? markDetails : Array(5).fill(''),
            } : {}),
            ...((restItem.type === 9) ? { 
                    mark_details: markDetails ? markDetails : Array(10).fill(''),
            } : {}),
            ...restItem,
        } :
        {
            candidates: candidatesPayload,
            is_required: isRequired,
            order: order ?? index,
            uuid,
            ...(restItem.type === 6 ? { 
                evaluation_scale_max: evaluationScaleMax,
                mark_details: markDetails ? markDetails : Array(evaluationScaleMax).fill(''),
            } : {}),
            ...(restItem.type === 8 ? { 
                slider_range_max: sliderRangeMax,
                slider_range_min: sliderRangeMin,
                slider_initial_user_value: sliderInitialUserValue,
                slider_range_max_detail: sliderRangeMaxDetail,
                slider_range_min_detail: sliderRangeMinDetail,
                slider_initial_user_value_detail: sliderInitialUserValueDetail,
                show_slider_value_details: showSliderValueDetails,
            } : {}),
            ...((restItem.type === 4) ? { 
                    mark_details: markDetails ? markDetails : Array(5).fill(''),
            } : {}),
            ...((restItem.type === 9) ? { 
                    mark_details: markDetails ? markDetails : Array(10).fill(''),
            } : {}),
            ...restItem,
        };
};
